import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStepNavigation } from '../../hooks/useStepNavigation';
import StepIndicator from '../../components/StepIndicator';
import { agendamentosService, type CreateAgendamentoDto } from '../../services/agendamentos.service';
import { colaboradoresService, type CreateColaboradorDto } from '../../services/colaboradores.service';
import { clinicasService } from '../../services/clinicas.service';
import { variacoesService } from '../../services/variacoes.service';
import { ordenarExamesPadrao, formatAsoTipo, formatarNomeExameAmigavel } from '../../utils/exames.utils';
import { DomainService } from '../../services/domain.service';
import { cargosService, type Cargo } from '../../services/cargos.service';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { useAuth } from '../../contexts/AuthContext';
import { solicitantesService } from '../../services/solicitantes.service';
import { formatCNPJ, formatCPF, maskEmail, maskTelefone } from '../../utils/formatters';
import { useActiveSubscription } from '../../hooks/useActiveSubscription';
import { useToast, ToastContainer } from '../../components/Toast';
import { limparRascunhoAgendamento } from '../../utils/agendamentoStorage';
import { Plus, Mail, Phone } from 'lucide-react';
import PageContainer from '../../components/PageContainer';
import api from '../../services/api';

interface CompanyData {
  ocupation: string;
  cnpj?: string;
  cpf?: string;
  nomeEmpresa: string;
  nomeEmpregador: string;
  email: string;
  telefone: string;
  pcmso?: string;
  nomeMedico?: string;
  crmMedico?: string;
  ufCRM?: string;
}

interface ExameDetalhado {
  id?: number;
  nome?: string;
  exame_id: number;
  exame_nome: string;
  clinica_id: number;
  clinica_nome: string;
  preco: number;
  obrigatorio: boolean;
  is_pontual?: boolean;
}

interface ColaboradorComExames {
  colaborador: {
    id: string;
    nome: string;
    cpf: string;
    nascimento?: string;
    sexo?: string;
    telefone?: string;
    email?: string;
    cargo?: string;
    setor?: string;
    formacao?: string;
    dataAdmissao?: string;
    demissao?: string;
    matricula_esocial?: string;
    clinica?: string;
    clinica_id?: string;
    clinicaNome?: string;
    dataAgendamento?: string;
    is_candidato?: boolean;
    escolha_clinica_tipo?: 'rh' | 'colaborador';
    exame?: string;
    variacao?: string;
    modeloExame?: string;
    tipoContrato?: string;
    cbo?: string;
    cargo_id?: number | string;
    setor_id?: number | string;
    is_existente?: boolean;
  };

  exame_id?: number;
  exame_nome?: string; // Nome do tipo de exame do banco
  exames: ExameDetalhado[];
  total: number;
  total_obrigatorios: number;
  total_optin: number;
}

interface ResumoEmployee {
  id?: string;
  nome: string;
  cpf: string;
  cargo?: string;
  cargo_id?: number | string;
  setor?: string;
  setor_id?: number | string;
  exame?: string;
  examesSelecionados?: number[];
  examesSelecionadosCount?: number;
  protocoloExames?: number[];
  examesPontuais?: number[];
  escolha_clinica_tipo?: 'rh' | 'colaborador';
  clinica?: string;
  clinicaNome?: string;
  dataAgendamento?: string;
  variacao?: string;
  is_candidato?: boolean;
  is_existente?: boolean;
  demissao?: string;
  [key: string]: unknown;
}

export default function ResumoPage() {
  const navigate = useNavigate();
  const { currentStep, stepData, saveStepData, goToPreviousStep, loadStepData } = useStepNavigation();
  const { empresa, empresas } = useEmpresa();
  const { user } = useAuth();
  const { toasts, error: showErrorToast, removeToast } = useToast();
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [colaboradoresComExames, setColaboradoresComExames] = useState<ColaboradorComExames[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setErrorState] = useState('');

  // Função helper para definir erro e mostrar toast
  const setError = (errorMessage: string) => {
    setErrorState(errorMessage);
    if (errorMessage && errorMessage.trim() !== '') {
      showErrorToast(errorMessage);
    }
  };

  // Verificação de assinatura usando hook
  const { subscription, isLoading: checkingSubscription } = useActiveSubscription();
  const hasSubscription = !!subscription;

  // Carregar dados da empresa quando empresa ou user mudarem
  const loadCompanyData = useCallback(async () => {
    try {
      // Usar empresa selecionada ou primeira disponível
      const empresaSelecionada = empresa || (empresas && empresas.length > 0 ? empresas[0] : null);

      if (!empresaSelecionada || !user) {
        console.warn('⚠️ [Resumo] Dados de empresa ou usuário não disponíveis');
        return;
      }

      let solicitanteNome = 'Nome não disponível';
      let solicitanteEmail = user.email; // Email do user pode ser usado como fallback
      let solicitanteTelefone = '';

      if (empresaSelecionada.solicitante_id) {
        try {
          const solicitante = await solicitantesService.findOne(empresaSelecionada.solicitante_id);
          if (solicitante) {
            solicitanteNome = (solicitante.nome && solicitante.nome.trim())
              || (solicitante.user?.name && solicitante.user.name.trim())
              || 'Nome não disponível';

            solicitanteEmail = (solicitante.email && solicitante.email.trim())
              || (solicitante.user?.email && solicitante.user.email.trim())
              || user.email;
            solicitanteTelefone = solicitante.telefone || '';
          }
        } catch (error) {
          console.error('❌ [Resumo] Erro ao buscar dados do solicitante:', error);
          try {
            const solicitantes = await solicitantesService.findAll({ limit: 10 });
            const solicitanteComNome = solicitantes.data.find(
              (s: { nome?: string; user?: { name?: string; email?: string }; email?: string; telefone?: string }) =>
                (s.nome && s.nome.trim()) || (s.user?.name && s.user.name.trim())
            );

            if (solicitanteComNome) {
              solicitanteNome = (solicitanteComNome.nome && solicitanteComNome.nome.trim())
                || (solicitanteComNome.user?.name && solicitanteComNome.user.name.trim())
                || 'Nome não disponível';
              solicitanteEmail = (solicitanteComNome.email && solicitanteComNome.email.trim())
                || (solicitanteComNome.user?.email && solicitanteComNome.user.email.trim())
                || user.email;
              solicitanteTelefone = solicitanteComNome.telefone || '';
            }
          } catch {
            solicitanteNome = 'Nome não disponível';
          }
        }
      } else {
        try {
          const solicitantes = await solicitantesService.findAll({ limit: 10 });
          const solicitanteComNome = solicitantes.data.find(
            (s: { nome?: string; user?: { name?: string; email?: string }; email?: string; telefone?: string }) =>
              (s.nome && s.nome.trim()) || (s.user?.name && s.user.name.trim())
          );

          if (solicitanteComNome) {
            solicitanteNome = (solicitanteComNome.nome && solicitanteComNome.nome.trim())
              || (solicitanteComNome.user?.name && solicitanteComNome.user.name.trim())
              || 'Nome não disponível';
            solicitanteEmail = (solicitanteComNome.email && solicitanteComNome.email.trim())
              || (solicitanteComNome.user?.email && solicitanteComNome.user.email.trim())
              || user.email;
            solicitanteTelefone = solicitanteComNome.telefone || '';
          }
        } catch {
          solicitanteNome = 'Nome não disponível';
        }
      }

      const ocupation = empresaSelecionada.cnpj ? 'PJ' : 'PF';

      const company: CompanyData = {
        ocupation,
        cnpj: empresaSelecionada.cnpj || undefined,
        nomeEmpresa: empresaSelecionada.nome,
        nomeEmpregador: solicitanteNome,
        email: solicitanteEmail,
        telefone: solicitanteTelefone || '(00) 00000-0000',
      };

      setCompanyData(company);
    } catch (error) {
      console.error('❌ [Resumo] Erro ao carregar dados da empresa:', error);
    }
  }, [empresa, empresas, user]);

  useEffect(() => {
    if (empresa || (empresas && empresas.length > 0)) {
      loadCompanyData();
    }
  }, [empresa, empresas, loadCompanyData]);

  const loadData = async () => {
    try {
      setLoading(true);

      const savedEmpresaId = localStorage.getItem('customer_employeesData_empresa_id');
      const currentEmpresaId = empresa?.id ? empresa.id.toString() : localStorage.getItem('customer_empresa_id');

      if (savedEmpresaId && currentEmpresaId && savedEmpresaId !== currentEmpresaId) {
        console.warn('⚠️ [Resumo] Empresa divergente do rascunho de agendamento. Limpando e redirecionando para o início.');
        limparRascunhoAgendamento();
        window.location.href = '/';
        return;
      }

      let employeesData = stepData.employeesData;
      const saved = localStorage.getItem('customer_employeesData');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed) && parsed.length > 0) {
            employeesData = parsed;
            saveStepData({ employeesData: parsed });
          }
        } catch (parseError) {
          console.error('❌ [Resumo] Erro ao parsear dados do localStorage:', parseError);
        }
      }

      if (!employeesData || employeesData.length === 0) {
        window.location.href = '/agendamento/cadastro';
        return;
      }

      await loadCompanyData();

      const targetEmpresaId = empresa?.id || (empresas && empresas.length > 0 ? empresas[0]?.id : undefined);

      // Buscar tipos de exames (Admissional, Demissional, etc.), catálogo geral de exames clínicos e cargos
      const [examTypes, catalogExames, allExamesClinicos, cargosRes] = await Promise.all([
        DomainService.getExamTypes().catch(() => []),
        clinicasService.getExamesClinicosClinica(1).catch(() => []),
        api.get<Array<{ id: number; nome: string }>>('/exames-clinicos').then((r) => r.data).catch(() => []),
        cargosService.findAll({ empresa_id: targetEmpresaId, limit: 1000 }).catch(() => ({ data: [], meta: { total: 0, page: 1, limit: 1000, totalPages: 0 } })),
      ]);

      const tiposExameMap: Record<number, string> = {
        1: 'Admissional',
        2: 'Demissional',
        3: 'Periódico',
        4: 'Retorno ao Trabalho',
        5: 'Mudança de Riscos Ocupacionais',
      };
      examTypes.forEach((t: { id: number; nome: string }) => {
        tiposExameMap[t.id] = t.nome;
      });

      const catalogMap = new Map<number, string>();
      let exameClinicoIdPadrao = 28;

      if (Array.isArray(allExamesClinicos)) {
        allExamesClinicos.forEach((e: { id: number; nome: string }) => {
          if (e?.id && e?.nome) catalogMap.set(Number(e.id), e.nome);
        });
      }

      catalogExames.forEach((e: { id: number; nome: string }) => {
        catalogMap.set(e.id, e.nome);
        const n = e.nome.toLowerCase().trim();
        if (n === 'exame clínico' || n === 'exame clinico') {
          exameClinicoIdPadrao = e.id;
        }
      });

      // Para cada colaborador, buscar detalhes dos exames
      const colaboradores: ColaboradorComExames[] = await Promise.all(
        employeesData.map(async (emp: {
          id: string;
          nome: string;
          cpf: string;
          nascimento?: string;
          sexo?: string;
          telefone?: string;
          email?: string;
          cargo?: string;
          cargo_id?: number | string;
          setor?: string;
          setor_id?: number | string;
          formacao?: string;
          dataAdmissao?: string;
          demissao?: string;
          matricula_esocial?: string;
          clinica?: string;
          clinica_id?: string;
          clinicaNome?: string;
          dataAgendamento?: string;
          exame?: string;
          variacao?: string;
          modeloExame?: string;
          tipoContrato?: string;
          cbo?: string;
          examesSelecionados?: number[];
          examesSelecionadosCount?: number;
          is_candidato?: boolean;
          escolha_clinica_tipo?: 'rh' | 'colaborador';
        }) => {
          const clinicaId = parseInt(emp.clinica || emp.clinica_id || '0');
          const isLinkColaborador = emp.escolha_clinica_tipo === 'colaborador' || !clinicaId;
          const isCandidateColab = Boolean(
            emp.is_candidato ||
            (emp as { is_existente?: boolean }).is_existente === false ||
            !emp.id ||
            String(emp.id).startsWith('local_') ||
            String(emp.id).startsWith('batch_'),
          );
          const exameId = emp.exame ? parseInt(emp.exame) : (isCandidateColab ? 1 : 3);
          if (!emp.exame || emp.exame === '') {
            emp.exame = String(exameId);
          }
          const tipoNome = tiposExameMap[exameId] || (isCandidateColab ? 'Admissional' : 'Exame Ocupacional');
          const isDemissional = exameId === 2 || tipoNome?.toLowerCase().includes('demiss');
          const isRetorno = exameId === 4 || tipoNome?.toLowerCase().includes('retorno');

          // 1. Identificar exames vinculados ao colaborador (estritamente pelo protocolo regulatório do cargo + setor + tipo exame)
          let idsExamesDoColaborador: number[] = emp.examesSelecionados ? [...emp.examesSelecionados] : [];

          const cargoIdNum = emp.cargo_id ? Number(emp.cargo_id) : undefined;
          let cargoMatch = cargoIdNum
            ? (cargosRes?.data as Cargo[])?.find((c) => c.id === cargoIdNum)
            : undefined;
          if (!cargoMatch && emp.cargo && cargosRes?.data) {
            cargoMatch = (cargosRes.data as Cargo[]).find(
              (c) => c.nome.toLowerCase().trim() === emp.cargo?.toLowerCase().trim()
            );
          }

          if (cargoMatch) {
            try {
              const cargoWithSetores = cargoMatch as Cargo & { setores?: Array<{ id: number; nome?: string }> };
              const targetSetorId = emp.setor_id
                ? Number(emp.setor_id)
                : (Array.isArray(cargoWithSetores.setores) && emp.setor
                    ? cargoWithSetores.setores.find((s) => s.nome?.toLowerCase().trim() === emp.setor?.toLowerCase().trim())?.id
                    : undefined);
              const resCargo = await cargosService.getExamesByCargo(cargoMatch.id, targetEmpresaId, targetSetorId, exameId);

              // Preservar exames: protocolo retornado da API ou pré-selecionados na etapa anterior
              const protocolIds = Array.isArray(resCargo.exames_clinicos_ids) && resCargo.exames_clinicos_ids.length > 0
                ? [...resCargo.exames_clinicos_ids]
                : (Array.isArray(emp.examesSelecionados) && emp.examesSelecionados.length > 0 ? [...emp.examesSelecionados] : []);
              const empTyped = emp as ResumoEmployee;
              const pontuaisIds = Array.isArray(empTyped.examesPontuais)
                ? empTyped.examesPontuais.filter((id: number) => !protocolIds.includes(id))
                : (emp.examesSelecionados || []).filter((id: number) => !protocolIds.includes(id));

              idsExamesDoColaborador = Array.from(new Set([...protocolIds, ...pontuaisIds]));
              emp.examesSelecionados = idsExamesDoColaborador;
              empTyped.examesPontuais = pontuaisIds;
              emp.examesSelecionadosCount = idsExamesDoColaborador.length;

              if (resCargo.variacoes && resCargo.variacoes.length > 0 && (!emp.variacao || emp.variacao === '1')) {
                emp.variacao = resCargo.variacoes[0].id.toString();
              }
              if (resCargo.exames) {
                resCargo.exames.forEach((ex: { id?: number; nome?: string }) => {
                  if (ex?.id && ex?.nome) {
                    catalogMap.set(ex.id, ex.nome);
                  }
                });
              }
            } catch (err) {
              console.warn('⚠️ [Resumo] Erro ao buscar exames do cargo:', err);
            }
          } else if (emp.examesSelecionados && emp.examesSelecionados.length > 0) {
            idsExamesDoColaborador = [...emp.examesSelecionados];
          } else if (isDemissional || isRetorno) {
            idsExamesDoColaborador = [];
          }

          if (isLinkColaborador) {
            const examesDetalhados: ExameDetalhado[] = [];

            // A) Exame Clínico (sempre presente e obrigatório)
            examesDetalhados.push({
              id: exameClinicoIdPadrao,
              exame_id: exameClinicoIdPadrao,
              exame_nome: 'Exame Clínico Ocupacional (ASO)',
              clinica_id: 0,
              clinica_nome: 'A definir pelo colaborador via link',
              preco: 0,
              obrigatorio: true,
            });

            // B) Exames complementares (Coprocultura, PPF, etc.) e Pontuais
            const pontuaisIdsList: number[] = Array.isArray((emp as ResumoEmployee).examesPontuais)
              ? ((emp as ResumoEmployee).examesPontuais as number[])
              : [];
            for (const examId of idsExamesDoColaborador) {
              if (examId === exameClinicoIdPadrao) continue;

              const isPontual = pontuaisIdsList.includes(examId);
              const nomeExame = catalogMap.get(examId) || `Exame Complementar #${examId}`;
              examesDetalhados.push({
                id: examId,
                exame_id: examId,
                exame_nome: formatarNomeExameAmigavel(nomeExame),
                clinica_id: 0,
                clinica_nome: 'A definir pelo colaborador via link',
                preco: 0,
                obrigatorio: !isPontual,
                is_pontual: isPontual,
              });
            }

            const examesOrdenados = ordenarExamesPadrao(examesDetalhados);

            return {
              colaborador: {
                ...emp,
                id: emp.id,
                nome: emp.nome,
                cpf: emp.cpf,
                nascimento: emp.nascimento,
                sexo: emp.sexo,
                telefone: emp.telefone,
                email: emp.email,
              },
              exame_id: exameId,
              exame_nome: tipoNome,
              exames: examesOrdenados,
              total: 0,
              total_obrigatorios: 0,
              total_optin: 0,
            };
          }

          // Auto-heal tipo de exame se ausente (Candidatos = Admissional, Ativos = Periódico)
          if (!emp.exame || emp.exame === '') {
            emp.exame = (emp.is_candidato || !emp.dataAdmissao) ? '1' : '3';
          }
          if (!exameId || isNaN(exameId)) {
            console.error(`❌ ERRO: tipo de exame inválido para ${emp.nome}:`, emp.exame);
            throw new Error(`Tipo de exame inválido para ${emp.nome}`);
          }

          try {
            // Buscar exames da clínica
            const clinicaExames = await clinicasService.getExamesClinicosClinica(clinicaId);
            const examesDetalhados: ExameDetalhado[] = [];

            // A) Exame Clínico (SEMPRE presente e obrigatório)
            const exameClinico = clinicaExames.find(
              e => e.nome.toLowerCase().trim() === 'exame clínico' || e.nome.toLowerCase().trim() === 'exame clinico'
            );
            if (exameClinico) {
              examesDetalhados.push({
                id: exameClinico.id,
                exame_id: exameClinico.id,
                exame_nome: formatarNomeExameAmigavel(exameClinico.nome),
                clinica_id: clinicaId,
                clinica_nome: emp.clinicaNome || '',
                preco: exameClinico.preco || 0,
                obrigatorio: true
              });
            } else {
              examesDetalhados.push({
                id: exameClinicoIdPadrao,
                exame_id: exameClinicoIdPadrao,
                exame_nome: 'Exame Clínico Ocupacional (ASO)',
                clinica_id: clinicaId,
                clinica_nome: emp.clinicaNome || '',
                preco: 0,
                obrigatorio: true
              });
            }

            // B) Exames selecionados / previstos pelo PGR/PCMSO e Pontuais
            const pontuaisIdsList: number[] = Array.isArray((emp as ResumoEmployee).examesPontuais)
              ? ((emp as ResumoEmployee).examesPontuais as number[])
              : [];
            for (const examId of idsExamesDoColaborador) {
              if (examId === exameClinicoIdPadrao || (exameClinico && examId === exameClinico.id)) continue;

              const isPontual = pontuaisIdsList.includes(examId);
              const exameEncontrado = clinicaExames.find(e => e.id === examId);
              if (exameEncontrado) {
                if (!examesDetalhados.some(ex => ex.id === examId)) {
                  examesDetalhados.push({
                    id: examId,
                    exame_id: examId,
                    exame_nome: formatarNomeExameAmigavel(exameEncontrado.nome),
                    clinica_id: clinicaId,
                    clinica_nome: emp.clinicaNome || '',
                    preco: exameEncontrado.preco || 0,
                    obrigatorio: !isPontual,
                    is_pontual: isPontual,
                  });
                }
              } else {
                const nomeExame = catalogMap.get(examId) || `Exame Complementar #${examId}`;
                if (!examesDetalhados.some(ex => ex.id === examId)) {
                  examesDetalhados.push({
                    id: examId,
                    exame_id: examId,
                    exame_nome: formatarNomeExameAmigavel(nomeExame),
                    clinica_id: clinicaId,
                    clinica_nome: emp.clinicaNome || '',
                    preco: 0,
                    obrigatorio: !isPontual,
                    is_pontual: isPontual,
                  });
                }
              }
            }

            // C) Exames da variação
            if (emp.variacao && emp.variacao !== '1') {
              try {
                const examesObrigatorios = await variacoesService.getExamesClinicosPorVariacaoClinica(
                  parseInt(emp.variacao),
                  clinicaId
                );
                console.log(`📋 [Resumo] Exames obrigatórios da variação ${emp.variacao} para ${emp.nome}:`, examesObrigatorios.length);

                examesObrigatorios.forEach(e => {
                  if (!examesDetalhados.some(ex => ex.id === e.id)) {
                    examesDetalhados.push({
                      id: e.id,
                      exame_id: e.id,
                      exame_nome: formatarNomeExameAmigavel(e.nome),
                      clinica_id: clinicaId,
                      clinica_nome: emp.clinicaNome || '',
                      preco: e.preco || 0,
                      obrigatorio: true
                    });
                  } else {
                    const index = examesDetalhados.findIndex(ex => ex.id === e.id);
                    if (index >= 0) {
                      examesDetalhados[index].obrigatorio = true;
                    }
                  }
                });
              } catch (error) {
                console.error(`❌ [Resumo] Erro ao buscar exames obrigatórios para ${emp.nome}:`, error);
              }
            }

            // Ordenar exames seguindo a regra de ouro
            const examesOrdenados = ordenarExamesPadrao(examesDetalhados);

            const total = examesOrdenados.reduce((sum, ex) => sum + ex.preco, 0);
            const totalObrigatorios = examesOrdenados.filter(e => e.obrigatorio).reduce((sum, ex) => sum + ex.preco, 0);
            const totalOptin = examesOrdenados.filter(e => !e.obrigatorio).reduce((sum, ex) => sum + ex.preco, 0);

            console.log(`✅ [Resumo] Total de exames para ${emp.nome}:`, examesOrdenados.length, 'Total:', total);

            return {
              colaborador: {
                ...emp,
                id: emp.id,
                nome: emp.nome,
                cpf: emp.cpf,
                nascimento: emp.nascimento,
                sexo: emp.sexo,
                telefone: emp.telefone,
                email: emp.email,
              },
              exame_id: exameId,
              exame_nome: tipoNome,
              exames: examesOrdenados,
              total,
              total_obrigatorios: totalObrigatorios,
              total_optin: totalOptin,
            };
          } catch (error) {
            console.error(`❌ [Resumo] Erro ao buscar exames para ${emp.nome}:`, error);
            const exameIdFallback = typeof exameId !== 'undefined' ? exameId : 0;
            return {
              colaborador: {
                ...emp,
                id: emp.id,
                nome: emp.nome,
                cpf: emp.cpf,
                nascimento: emp.nascimento,
                sexo: emp.sexo,
                telefone: emp.telefone,
                email: emp.email,
              },
              exame_id: exameIdFallback,
              exame_nome: tipoNome,
              exames: [],
              total: 0,
              total_obrigatorios: 0,
              total_optin: 0,
            };
          }
        })
      );

      setColaboradoresComExames(colaboradores);

    } catch (error: unknown) {
      const err = error as { message?: string };
      console.error('Erro ao carregar dados:', error);
      setError(err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStepData();
    const timer = setTimeout(() => {
      loadData();
    }, 50);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevEmpresaIdRef = useRef<number | null>(empresa?.id ?? null);

  // Monitorar se a empresa ativa mudar enquanto estiver na tela de resumo
  useEffect(() => {
    if (!empresa?.id) return;
    const currentEmpresaId = empresa.id.toString();
    const savedEmpresaId = localStorage.getItem('customer_employeesData_empresa_id');

    const empresaMudou = prevEmpresaIdRef.current !== null && prevEmpresaIdRef.current !== empresa.id;
    const rascunhoDivergente = Boolean(savedEmpresaId && savedEmpresaId !== currentEmpresaId);

    if (empresaMudou || rascunhoDivergente) {
      console.log('🔄 [Resumo] Empresa alterada durante resumo. Limpando rascunho e redirecionando para início.');
      limparRascunhoAgendamento();
      window.location.href = '/';
      return;
    }

    prevEmpresaIdRef.current = empresa.id;
  }, [empresa?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🔄 [Resumo] handleSubmit chamado!', {
      saving,
      colaboradoresComExames: colaboradoresComExames.length,
      hasSubscription,
      checkingSubscription,
    });

    setSaving(true);
    setError('');

    try {
      console.log('🔄 [Resumo] Iniciando finalização do pedido...');

      // Validar se há colaboradores com exames
      if (colaboradoresComExames.length === 0) {
        setError('Nenhum colaborador encontrado. Por favor, adicione colaboradores no cadastro.');
        setSaving(false);
        return;
      }

      console.log('📊 [Resumo] Dados antes de enviar:', {
        colaboradoresComExames: colaboradoresComExames.length,
        stepDataEmployees: stepData.employeesData?.length || 0
      });

      // Converter data de DD/MM/YYYY para YYYY-MM-DD
      const formatDateForAPI = (dateStr?: string | null): string | undefined => {
        if (!dateStr || typeof dateStr !== 'string') return undefined;
        const clean = dateStr.trim();
        if (!clean) return undefined;
        if (clean.match(/^\d{4}-\d{2}-\d{2}$/)) return clean;
        if (clean.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
          const [dia, mes, ano] = clean.split('/');
          return `${ano}-${mes}-${dia}`;
        }
        if (clean.includes('T')) {
          return clean.split('T')[0];
        }
        return undefined;
      };

      // 0. Auto-persistir na API colaboradores que ainda possuam ID local
      let updatedStepEmployees = false;
      for (const col of colaboradoresComExames) {
        const colIdNum = parseInt(String(col.colaborador.id));
        if (isNaN(colIdNum) || String(col.colaborador.id).startsWith('local_')) {
          const emp = col.colaborador;

          const cleanCpf = String(emp.cpf || '').replace(/\D/g, '');
          const nascimentoFormatted = formatDateForAPI(emp.nascimento) || '2000-01-01';

          const createData: CreateColaboradorDto = {
            empresa_id: empresa?.id,
            nome: String(emp.nome || '').trim(),
            cpf: cleanCpf,
            data_nascimento: nascimentoFormatted,
            sexo: ((emp.sexo as string) || 'M').toUpperCase() as 'M' | 'F',
            telefone: emp.telefone && String(emp.telefone).trim() !== '' ? String(emp.telefone).trim() : undefined,
            email: emp.email && String(emp.email).trim() !== '' ? String(emp.email).trim() : undefined,
            is_candidato: Boolean(emp.is_candidato),
          };
          if (emp.formacao) createData.formacao_academica_id = parseInt(String(emp.formacao));
          if (emp.dataAdmissao) {
            const adm = formatDateForAPI(String(emp.dataAdmissao));
            if (adm) createData.data_admissao = adm;
          }
          if (emp.demissao) {
            const dem = formatDateForAPI(String(emp.demissao));
            if (dem) createData.data_demissao = dem;
          }
          if (emp.matricula_esocial) createData.matricula_esocial = String(emp.matricula_esocial).trim();
          if (emp.cargo_id) createData.cargo_id = Number(emp.cargo_id);
          if (emp.setor_id) createData.setor_id = Number(emp.setor_id);
          if (emp.cargo) createData.cargo_nome = String(emp.cargo).trim();
          if (emp.setor) createData.setor_nome = String(emp.setor).trim();

          try {
            const criado = await colaboradoresService.create(createData);
            if (criado?.id) {
              const newIdStr = criado.id.toString();
              col.colaborador.id = newIdStr;
              updatedStepEmployees = true;
            }
          } catch (errCreate: unknown) {
            // Se o colaborador já existe nesta empresa (ex: já cadastrado na base ou SGG), reaproveitar o ID existente
            let resolvedExisting = false;
            try {
              const resExisting = await colaboradoresService.findAll({
                search: cleanCpf,
                empresa_id: empresa?.id,
                enabled: 'all',
                limit: 5,
              });
              const match = (resExisting?.data || []).find(
                (c) => (c.cpf || '').replace(/\D/g, '') === cleanCpf,
              );
              if (match?.id) {
                const newIdStr = match.id.toString();
                col.colaborador.id = newIdStr;
                col.colaborador.is_existente = true;
                updatedStepEmployees = true;
                resolvedExisting = true;
                console.log(`✅ [Resumo] Colaborador ${match.nome} (CPF ${cleanCpf}) vinculado automaticamente ao ID existente #${match.id}.`);
              }
            } catch (errFind) {
              console.warn('⚠️ [Resumo] Falha ao tentar buscar colaborador existente por CPF:', errFind);
            }

            if (!resolvedExisting) {
              const errResponse = (errCreate as { response?: { data?: { message?: string | string[] } }; message?: string });
              const errDetails = errResponse?.response?.data?.message || errResponse?.message;
              const formatted = Array.isArray(errDetails) ? errDetails.join(', ') : errDetails;
              throw new Error(`Não foi possível cadastrar o colaborador ${String(emp.nome || '')}: ${formatted || 'Erro desconhecido'}`);
            }
          }
        }
      }

      // 0.1 Atualizar data de demissão para colaboradores já existentes se exame for demissional
      for (const col of colaboradoresComExames) {
        const emp = col.colaborador;
        const colIdNum = parseInt(String(emp.id));
        if (!isNaN(colIdNum) && !String(emp.id).startsWith('local_')) {
          const tipoExameId = emp?.exame ? parseInt(String(emp.exame)) : NaN;
          if (tipoExameId === 2 && emp.demissao) {
            const dem = formatDateForAPI(String(emp.demissao));
            if (dem) {
              try {
                await colaboradoresService.update(colIdNum, { data_demissao: dem, enabled: false });
              } catch (errDem: unknown) {
                console.warn(`⚠️ [Resumo] Não foi possível atualizar data_demissao do colaborador ${colIdNum}:`, errDem);
              }
            }
          }
        }
      }

      if (updatedStepEmployees) {
        const updatedEmployeesList = colaboradoresComExames.map(c => c.colaborador);
        saveStepData({ employeesData: updatedEmployeesList });
        localStorage.setItem('customer_employeesData', JSON.stringify(updatedEmployeesList));
        if (empresa?.id) {
          localStorage.setItem('customer_employeesData_empresa_id', empresa.id.toString());
        }
      }

      // Construir dados para criar agendamento usando colaboradoresComExames (já tem todos os exames)
      const agendamentosData = colaboradoresComExames.map((col) => {
        const emp = col.colaborador;

        // Auto-heal tipo de exame se ausente no momento da confirmação
        if (!emp?.exame || emp.exame === '') {
          emp.exame = (emp?.is_candidato || !emp?.dataAdmissao) ? '1' : '3';
        }
        const tipoExameId = parseInt(String(emp.exame));

        // Validar que é um número válido
        if (!tipoExameId || isNaN(tipoExameId)) {
          throw new Error(`Tipo de exame inválido para colaborador: ${emp?.nome || 'desconhecido'}`);
        }

        // Validar ID do colaborador e garantir que é um número
        const colaboradorId = col.colaborador.id ? parseInt(String(col.colaborador.id)) : NaN;

        if (isNaN(colaboradorId)) {
          console.error(`❌ ERRO: ID de colaborador inválido: ${col.colaborador.id}`, col.colaborador);
          throw new Error(`Dados inválidos para o colaborador ${col.colaborador.nome}. ID ausente ou inválido.`);
        }

        const escolhaTipo: 'colaborador' | 'rh' = emp?.escolha_clinica_tipo === 'colaborador' ? 'colaborador' : 'rh';
        const hasClinica = emp?.clinica && emp.clinica !== '0' && emp.clinica !== '';
        return {
          colaborador_id: colaboradorId,
          clinica_id: hasClinica ? parseInt(String(emp.clinica)) : (escolhaTipo === 'colaborador' ? undefined : parseInt(String(emp?.clinica_id || '1'))),
          variacao_id: parseInt(String(emp?.variacao || '1')),
          exame_id: tipoExameId, // SEMPRE preenchido e validado
          data_agendamento: hasClinica && emp?.dataAgendamento ? formatDateForAPI(String(emp.dataAgendamento)) : undefined,
          exames_clinicos_ids: col.exames.map((ex) => ex.id!).filter(id => id != null),
          online: emp?.modeloExame === '1' ? 1 : 0, // '1' = online, '0' = presencial
          escolha_clinica_tipo: escolhaTipo,
          cargo_id: emp?.cargo_id ? parseInt(String(emp.cargo_id)) : undefined,
          setor_id: emp?.setor_id ? parseInt(String(emp.setor_id)) : undefined,
          data_demissao: tipoExameId === 2 && emp?.demissao ? formatDateForAPI(String(emp.demissao)) : undefined,
        };
      }).filter((ag: { exames_clinicos_ids: number[] }) => ag.exames_clinicos_ids.length > 0);

      if (agendamentosData.length === 0) {
        setError('Nenhum colaborador com exames encontrado. Por favor, adicione exames aos colaboradores.');
        setSaving(false);
        return;
      }

      // Agendamento Corporativo (Customer Portal):
      // Todo faturamento é pós-pago e consolidado no contrato mensal da empresa
      const tipoPagamento = 'faturamento';

      const createAgendamentoDto: CreateAgendamentoDto = {
        agendamentos: agendamentosData,
        tipo_pagamento: tipoPagamento,
      };

      console.log('📤 [Resumo] Enviando requisição corporativa:', {
        agendamentosCount: createAgendamentoDto.agendamentos.length,
        primeiroAgendamento: createAgendamentoDto.agendamentos[0],
        todosAgendamentos: createAgendamentoDto.agendamentos,
        dtoCompleto: JSON.stringify(createAgendamentoDto, null, 2),
      });

      // Criar agendamento
      console.log('🔄 [Resumo] Chamando agendamentosService.create()...');
      const response = await agendamentosService.create(createAgendamentoDto);
      console.log('✅ [Resumo] Resposta recebida do service:', {
        pedido_id: response.pedido_id,
        codigo_pedido: response.codigo_pedido,
        valor_total: response.valor_total,
        agendamentos_count: response.agendamentos_count,
        respostaCompleta: response,
      });

      // Verificar se a resposta tem a estrutura esperada
      console.log('🔍 [Resumo] Validando resposta da API...');
      console.log('🔍 [Resumo] Estrutura da resposta:', {
        temPedidoId: !!response.pedido_id,
        pedidoId: response.pedido_id,
        tipoPedidoId: typeof response.pedido_id,
        temCodigoPedido: !!response.codigo_pedido,
        codigoPedido: response.codigo_pedido,
        temValorTotal: !!response.valor_total,
        valorTotal: response.valor_total,
        respostaCompleta: response,
      });

      if (!response.pedido_id || !response.codigo_pedido) {
        console.error('❌ [Resumo] Resposta inválida da API - campos obrigatórios faltando:', {
          pedido_id: response.pedido_id,
          codigo_pedido: response.codigo_pedido,
          respostaCompleta: response,
        });
        throw new Error('Resposta inválida da API: pedido_id ou codigo_pedido não encontrados');
      }

      // Garantir que pedido_id é um número
      const pedidoId = typeof response.pedido_id === 'bigint'
        ? Number(response.pedido_id)
        : Number(response.pedido_id);

      if (isNaN(pedidoId)) {
        console.error('❌ [Resumo] pedido_id não é um número válido:', response.pedido_id);
        throw new Error('Resposta inválida da API: pedido_id não é um número válido');
      }

      console.log('✅ [Resumo] Resposta validada com sucesso:', {
        pedido_id: pedidoId,
        codigo_pedido: response.codigo_pedido,
        valor_total: response.valor_total,
      });

      // Limpar completamente o localStorage de todos os dados relacionados ao rascunho de agendamento
      console.log('🧹 [Resumo] Limpando todo o rascunho do agendamento após confirmação com sucesso...');
      limparRascunhoAgendamento();

      const redirectUrl = `/agendamento/final/${response.codigo_pedido}`;
      console.log('➡️ [Resumo] Redirecionando para página final:', redirectUrl);
      window.location.href = redirectUrl;

    } catch (error: unknown) {
      const err = error as { message?: string; response?: { data?: { message?: string } }; stack?: string };
      console.error('❌ [Resumo] Erro completo:', {
        error,
        message: err?.message,
        response: err?.response,
        responseData: err?.response?.data,
        stack: err?.stack
      });
      const errorMessage = err?.response?.data?.message || err?.message || 'Erro ao finalizar agendamento. Por favor, tente novamente.';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };



  if (loading && colaboradoresComExames.length === 0) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
        {/* Header com informações da empresa */}
        {companyData && (
          <div className="bg-white rounded-xl shadow-lg border border-green-100 p-8 mb-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-4">
                <h1 className="text-2xl font-bold text-gray-900">Resumo do Agendamento</h1>
                <p className="text-gray-600">Revise os colaboradores e exames antes de confirmar o agendamento corporativo</p>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm3 2a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-green-700 uppercase tracking-wide">Empresa</span>
                    <p className="text-sm font-semibold text-gray-900">{companyData.nomeEmpresa}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 011.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-green-700 uppercase tracking-wide">
                      {companyData.ocupation === 'PJ' ? 'CNPJ' : 'CPF'}
                    </span>
                    <p className="text-sm font-semibold text-gray-900">
                      {companyData.ocupation === 'PJ'
                        ? formatCNPJ(companyData.cnpj || '')
                        : formatCPF(companyData.cpf || '')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-green-700 uppercase tracking-wide">Email</span>
                    <p className="text-sm font-semibold text-gray-900">{companyData.email}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step indicator */}
        <StepIndicator currentStep={currentStep} />

        {loading && (
          <div className="bg-white rounded-xl shadow-lg border border-green-100 p-8 mb-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando dados...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {!loading && (
          <form onSubmit={(e) => {
            console.log('📝 [Resumo] Form onSubmit chamado!');
            handleSubmit(e);
          }}>

            {/* Resumo de Colaboradores e Exames - Layout Corporativo Full Width */}
            <div className="w-full mb-8">
              <div className="bg-white rounded-xl shadow-lg border border-green-100 p-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-4 border-b border-gray-100">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      Resumo dos Colaboradores e Exames
                      {colaboradoresComExames.length > 0 && (
                        <span className="ml-2 text-base font-normal text-gray-600">
                          ({colaboradoresComExames.length} {colaboradoresComExames.length === 1 ? 'colaborador integrado' : 'colaboradores integrados'})
                        </span>
                      )}
                    </h2>
                    <p className="text-xs text-gray-500 mt-1">
                      Confira os dados dos exames antes de confirmar o agendamento corporativo.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate('/agendamento/cadastro?tab=lote_manual')}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-xs font-bold rounded-xl shadow-sm hover:shadow transition-all shrink-0"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Incluir Mais Agendamentos</span>
                  </button>
                </div>

                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                    <p className="text-gray-600 mt-4">Carregando colaboradores e exames...</p>
                  </div>
                ) : colaboradoresComExames.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600">Nenhum colaborador encontrado no pedido.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {colaboradoresComExames.map((colaborador) => {
                      // Filtrar exames - garantir que sempre temos um array válido
                      const examesValidos = (colaborador.exames || []).filter((e: ExameDetalhado) => e && (e.exame_nome || e.nome))

                      return (
                        <div key={colaborador.colaborador.id} className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                          {/* Cabeçalho do colaborador */}
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 pb-4 border-b-2 border-gray-200 gap-4">
                            <div>
                              <h3 className="text-lg font-bold text-gray-900">{colaborador.colaborador.nome}</h3>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-gray-600 mt-2 font-mono">
                                <span>CPF: {formatCPF(colaborador.colaborador.cpf)}</span>
                                {colaborador.colaborador.email ? (
                                  <span
                                    className="font-sans text-gray-700 bg-white border border-gray-200 px-2 py-0.5 rounded text-[11px] flex items-center gap-1.5 shadow-2xs"
                                    title={`E-mail: ${colaborador.colaborador.email}`}
                                  >
                                    <Mail className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                    <span>{maskEmail(colaborador.colaborador.email)}</span>
                                  </span>
                                ) : (
                                  <span className="font-sans text-gray-400 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded text-[11px] flex items-center gap-1.5">
                                    <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                    <span>Sem e-mail</span>
                                  </span>
                                )}
                                {colaborador.colaborador.telefone ? (
                                  <span
                                    className="font-sans text-gray-700 bg-white border border-gray-200 px-2 py-0.5 rounded text-[11px] flex items-center gap-1.5 shadow-2xs"
                                    title={`Telefone: ${colaborador.colaborador.telefone}`}
                                  >
                                    <Phone className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                    <span>{maskTelefone(colaborador.colaborador.telefone)}</span>
                                  </span>
                                ) : (
                                  <span className="font-sans text-gray-400 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded text-[11px] flex items-center gap-1.5">
                                    <Phone className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                    <span>Sem telefone</span>
                                  </span>
                                )}
                              </div>
                              {colaborador.exame_nome && (
                                <p className="text-sm text-gray-600 mt-2">
                                  Tipo: <span className="font-bold text-gray-900">{formatAsoTipo(colaborador.exame_nome)}</span>
                                </p>
                              )}
                            </div>
                            <div className="text-left sm:text-right">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                Faturamento no Contrato
                              </span>
                            </div>
                          </div>

                          {/* Exames Selecionados */}
                          <div>
                            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-3 gap-2 md:gap-0">
                              <h4 className="text-sm font-semibold text-gray-700">
                                Exames Inclusos ({examesValidos.length})
                              </h4>
                            </div>
                            {examesValidos.length > 0 ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {examesValidos.map((exame: ExameDetalhado, index: number) => (
                                  <div
                                    key={index}
                                    className={`p-3 border rounded-lg ${exame.obrigatorio
                                      ? 'border-emerald-300 bg-emerald-50/60'
                                      : 'border-gray-200 bg-white'
                                      }`}
                                  >
                                    <div className="flex items-start justify-between mb-2 flex-wrap gap-2">
                                      <p className="font-medium text-sm text-gray-900 flex-1">
                                        {exame.exame_nome || exame.nome || 'Exame'}
                                      </p>
                                      {exame.is_pontual ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800 border border-indigo-200 whitespace-nowrap flex-shrink-0">
                                          Pontual / Avulso
                                        </span>
                                      ) : exame.obrigatorio ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300 whitespace-nowrap flex-shrink-0">
                                          PGR/PCMSO
                                        </span>
                                      ) : null}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {exame.clinica_nome || 'Clínica credenciada'}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">Exame Clínico Ocupacional incluso.</p>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>


            {/* Navegação */}
            <div className="flex flex-col md:flex-row justify-between items-center mt-8 gap-4">
              {/* Botão Voltar (Mobile: Line 3 / Desktop: Left) */}
              <button
                type="button"
                onClick={goToPreviousStep}
                className="w-full md:w-auto flex justify-center items-center px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-semibold order-3 md:order-1"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Voltar
              </button>

              {/* Indicador de progresso (Mobile: Link 1 / Desktop: Center) */}
              <div className="flex items-center space-x-2 order-1 md:order-2 mb-2 md:mb-0">
                {[1, 2, 3].map((step) => (
                  <div
                    key={step}
                    className={`w-3 h-3 rounded-full ${step === currentStep
                      ? 'bg-green-600'
                      : step < currentStep
                        ? 'bg-green-400'
                        : 'bg-gray-300'
                      }`}
                  />
                ))}
                <span className="ml-3 text-sm text-gray-600 font-medium">
                  Passo {currentStep} de 3
                </span>
              </div>

              {/* Ações à Direita: Incluir Mais + Confirmar (Mobile: Line 2 / Desktop: Right) */}
              <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto order-2 md:order-3">
                <button
                  type="button"
                  onClick={() => navigate('/agendamento/cadastro?tab=lote_manual')}
                  className="w-full sm:w-auto inline-flex justify-center items-center gap-2 px-5 py-3 border-2 border-emerald-500/30 bg-emerald-50 hover:bg-emerald-100 active:bg-emerald-200 text-emerald-800 rounded-lg transition-colors font-bold text-sm shadow-sm"
                >
                  <Plus className="w-4 h-4 text-emerald-700" />
                  <span>Incluir Mais Agendamentos</span>
                </button>

                <button
                  type="submit"
                  disabled={saving || colaboradoresComExames.length === 0}
                  onClick={() => {
                    console.log('🖱️ [Resumo] Botão Finalizar Pedido clicado!', {
                      saving,
                      colaboradoresComExames: colaboradoresComExames.length,
                      disabled: saving || colaboradoresComExames.length === 0,
                    });
                    // Não prevenir default aqui, deixar o form onSubmit tratar
                  }}
                  className="w-full sm:w-auto flex justify-center items-center px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green-600 transition-colors font-semibold shadow-md"
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processando...
                    </>
                  ) : (
                    <>
                      Confirmar Agendamento
                      <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        )}

      {/* Toast Container para notificações */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </PageContainer>
  );
}
